import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNotifications } from '../context/NotificationContext';
import '../assets/css/stay-connected-popup.css';

const StayConnectedPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const { showCustomToast } = useNotifications();

  // Sync with app dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      const darkModeEnabled = localStorage.getItem('autoboyDarkMode');
      const isDark = darkModeEnabled === 'true' || document.body.classList.contains('AutoboyExpress-dash-dark-mode');
      setIsDarkMode(isDark);
    };

    checkDarkMode();

    // Watch for dark mode changes
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // Skip for small screens if needed or if user is on critical pages (auth, checkout)
    const isCriticalPage = window.location.pathname.includes('/auth') || 
                          window.location.pathname.includes('/checkout') ||
                          window.location.pathname.includes('/seller-signup');
    
    if (isCriticalPage) return;

    // Check if user has already seen the popup in this session or historically
    const hasSeenPopup = localStorage.getItem('hasSeenStayConnectedPopup');
    const sessionSeen = sessionStorage.getItem('sessionSeenPopup');
    
    // Check for expiration (hidden for 7 days)
    const lastSeenTime = localStorage.getItem('stayConnectedPopupLastSeen');
    const isExpired = lastSeenTime && (Date.now() - parseInt(lastSeenTime) > 7 * 24 * 60 * 60 * 1000);

    if (!hasSeenPopup || isExpired) {
      if (sessionSeen) return;

      // Show popup after 10 seconds (less intrusive than 3s)
      const timer = setTimeout(() => {
        setIsVisible(true);
        sessionStorage.setItem('sessionSeenPopup', 'true');
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, []);

  // Prevent body scroll when popup is open
  useEffect(() => {
    if (isVisible) {
      document.body.classList.add('popup-open');
    } else {
      document.body.classList.remove('popup-open');
    }

    return () => {
      document.body.classList.remove('popup-open');
    };
  }, [isVisible]);

  const handleClose = () => {
    setIsVisible(false);
    localStorage.setItem('hasSeenStayConnectedPopup', 'true');
  };

  const handleSubscribe = (e) => {
    e.preventDefault();
    const email = e.target.email.value;

    // Here you would typically send to your backend

    // Show success notification
    showCustomToast({
      type: 'success',
      title: 'Successfully Subscribed!',
      message: `You'll now receive exclusive deals and updates at ${email}`,
      duration: 5000
    });

    // Close popup and mark as seen
    handleClose();
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Backdrop */}
          <motion.div
            className="stay-connected-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          >
            {/* Popup Container - Handles Centering */}
            <div className="stay-connected-popup-container" onClick={(e) => e.stopPropagation()}>
              {/* Popup - Handles Animation */}
              <motion.div
                className={`stay-connected-popup ${isDarkMode ? 'dark-mode' : ''}`}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{
                  type: "spring",
                  damping: 25,
                  stiffness: 300
                }}
              >
            {/* Close Button */}
            <button
              className="stay-connected-close"
              onClick={handleClose}
              aria-label="Close popup"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>

            {/* Decorative Elements */}
            <div className="stay-connected-orb orb-1"></div>
            <div className="stay-connected-orb orb-2"></div>
            <div className="stay-connected-orb orb-3"></div>

            {/* Content */}
            <div className="stay-connected-content">
              {/* Icon/Image */}
              <motion.div
                className="stay-connected-icon"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              >
                <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
                  <circle cx="40" cy="40" r="38" stroke="url(#gradient1)" strokeWidth="4"/>
                  <path d="M25 40L35 50L55 30" stroke="url(#gradient2)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
                  <defs>
                    <linearGradient id="gradient1" x1="0" y1="0" x2="80" y2="80">
                      <stop offset="0%" stopColor="#22C55E"/>
                      <stop offset="100%" stopColor="#16A34A"/>
                    </linearGradient>
                    <linearGradient id="gradient2" x1="25" y1="30" x2="55" y2="50">
                      <stop offset="0%" stopColor="#22C55E"/>
                      <stop offset="100%" stopColor="#10B981"/>
                    </linearGradient>
                  </defs>
                </svg>
              </motion.div>

              {/* Title */}
              <motion.h2
                className="stay-connected-title"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                Stay Connected with AutoboyExpress
              </motion.h2>

              {/* Description */}
              <motion.p
                className="stay-connected-description"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                Get exclusive deals, early access to new products, and personalized recommendations delivered to your inbox.
              </motion.p>

              {/* Benefits */}
              <motion.div
                className="stay-connected-benefits"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <div className="benefit-item">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M7 10L9 12L13 8" stroke="#22C55E" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <span>Exclusive Deals</span>
                </div>
                <div className="benefit-item">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M7 10L9 12L13 8" stroke="#22C55E" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <span>Early Access</span>
                </div>
                <div className="benefit-item">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M7 10L9 12L13 8" stroke="#22C55E" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <span>Price Alerts</span>
                </div>
              </motion.div>

              {/* Subscribe Form */}
              <motion.form
                className="stay-connected-form"
                onSubmit={handleSubscribe}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <div className="form-input-wrapper">
                  <svg className="input-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M3 4h14c1.1 0 2 .9 2 2v8c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M19 6l-9 6-9-6" stroke="currentColor" strokeWidth="1.5"/>
                  </svg>
                  <input
                    type="email"
                    name="email"
                    placeholder="Enter your email"
                    required
                    className="stay-connected-input"
                  />
                </div>
                <button type="submit" className="stay-connected-submit">
                  Subscribe
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M4 9h10m0 0l-4-4m4 4l-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              </motion.form>

              {/* Social Links */}
              <motion.div
                className="stay-connected-social"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
              >
                <span className="social-label">Follow us:</span>
                <div className="social-icons">
                  <a href="#" className="social-icon" aria-label="Facebook">
                    <i className="fab fa-facebook-f"></i>
                  </a>
                  <a href="#" className="social-icon" aria-label="X (Twitter)">
                    <i className="fab fa-x-twitter"></i>
                  </a>
                  <a href="#" className="social-icon" aria-label="Instagram">
                    <i className="fab fa-instagram"></i>
                  </a>
                  <a href="#" className="social-icon" aria-label="LinkedIn">
                    <i className="fab fa-linkedin-in"></i>
                  </a>
                </div>
              </motion.div>
            </div>
              </motion.div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default StayConnectedPopup;
